import type { Item } from '../api';

export type ExportFormat = 'markdown' | 'json' | 'csv';

/**
 * 将项目导出为 Markdown 格式
 */
function exportToMarkdown(items: Item[]): string {
  let markdown = '# SnapClip 导出\n\n';
  markdown += `导出时间：${new Date().toLocaleString('zh-CN')}\n\n`;
  markdown += '---\n\n';

  const pendingItems = items.filter(item => !item.is_completed);
  const completedItems = items.filter(item => item.is_completed);

  if (pendingItems.length > 0) {
    markdown += '## 待处理\n\n';
    pendingItems.forEach(item => {
      markdown += `### ${item.content}\n\n`;
      if (item.url) {
        markdown += `🔗 链接：${item.url}\n\n`;
      }
      if (item.source) {
        markdown += `📍 来源：${item.source}\n\n`;
      }
      if (item.notes) {
        markdown += `📝 备注：${item.notes}\n\n`;
      }
      markdown += `📅 创建时间：${new Date(item.created_at).toLocaleString('zh-CN')}\n\n`;
      markdown += '---\n\n';
    });
  }

  if (completedItems.length > 0) {
    markdown += '## 已完成\n\n';
    completedItems.forEach(item => {
      markdown += `### ~~${item.content}~~\n\n`;
      if (item.url) {
        markdown += `🔗 链接：${item.url}\n\n`;
      }
      if (item.source) {
        markdown += `📍 来源：${item.source}\n\n`;
      }
      if (item.notes) {
        markdown += `📝 备注：${item.notes}\n\n`;
      }
      markdown += `✅ 完成时间：${new Date(item.updated_at).toLocaleString('zh-CN')}\n\n`;
      markdown += '---\n\n';
    });
  }

  return markdown;
}

/**
 * 将项目导出为 JSON 格式
 */
function exportToJson(items: Item[]): string {
  return JSON.stringify({
    exportTime: new Date().toISOString(),
    count: items.length,
    items: items,
  }, null, 2);
}

/**
 * 将项目导出为 CSV 格式
 */
function exportToCsv(items: Item[]): string {
  const headers = ['ID', '内容', '链接', '来源', '备注', '状态', '创建时间', '更新时间'];
  const rows = items.map(item => [
    item.id.toString(),
    `"${item.content.replace(/"/g, '""')}"`, // 转义双引号
    item.url || '',
    item.source || '',
    `"${(item.notes || '').replace(/"/g, '""')}"`,
    item.is_completed ? '已完成' : '待处理',
    new Date(item.created_at).toLocaleString('zh-CN'),
    new Date(item.updated_at).toLocaleString('zh-CN'),
  ]);

  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}

/**
 * 下载文件
 */
function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 导出组合式函数
 */
export function useExport() {
  /**
   * 导出项目
   */
  function exportItems(items: Item[], format: ExportFormat) {
    const timestamp = new Date().toISOString().slice(0, 10);
    let content: string;
    let filename: string;
    let mimeType: string;

    switch (format) {
      case 'markdown':
        content = exportToMarkdown(items);
        filename = `snapclip-export-${timestamp}.md`;
        mimeType = 'text/markdown';
        break;
      case 'json':
        content = exportToJson(items);
        filename = `snapclip-export-${timestamp}.json`;
        mimeType = 'application/json';
        break;
      case 'csv':
        content = exportToCsv(items);
        filename = `snapclip-export-${timestamp}.csv`;
        mimeType = 'text/csv';
        break;
      default:
        throw new Error(`不支持的导出格式: ${format}`);
    }

    downloadFile(content, filename, mimeType);
  }

  return {
    exportItems,
  };
}
